<!-- Trigger button -->
<button
  class="relative w-9 h-9 rounded-full border-0 bg-white shadow-card flex items-center justify-center cursor-pointer transition-all duration-150 text-gray-text hover:scale-110 hover:shadow-card"
  [ngClass]="{
    'text-accent-secondary animate-pulse': status() === 'running',
    'text-st-tick': status() === 'passed',
    'text-danger': status() === 'failed'
  }"
  (click)="panelRef.toggle($event, $event.currentTarget); onTriggerClick()"
  [matTooltip]="'e2eTestsDevOnly' | translate"
  matTooltipPosition="below"
  [attr.aria-label]="'openE2eTestRunner' | translate"
>
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="none"
    stroke="url(#flaskGradient)"
    stroke-width="2"
    stroke-linecap="round"
    stroke-linejoin="round"
  >
    <defs>
      <linearGradient id="flaskGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stop-color="var(--color-primary-start-color)" />
        <stop offset="100%" stop-color="var(--color-primary-end-color)" />
      </linearGradient>
    </defs>
    <path d="M9 3h6M9 3v8L5.5 17a2 2 0 0 0 1.8 3h9.4a2 2 0 0 0 1.8-3L15 11V3" />
    <line x1="9" y1="13" x2="15" y2="13" />
  </svg>

  <!-- Status dot badge -->
  <span
    class="absolute top-1 right-1 w-[7px] h-[7px] rounded-full border-[1.5px] border-white"
    [ngClass]="{
      'bg-light-text': status() === 'idle',
      'bg-accent-secondary animate-pulse': status() === 'running',
      'bg-st-tick': status() === 'passed',
      'bg-danger': status() === 'failed'
    }"
  ></span>
</button>

<!-- Panel via shared popover -->
<app-popover
  #panelRef
  direction="left"
  appendTo="body"
  [showArrow]="false"
  width="360px"
  [attr.aria-label]="'e2eTestRunner' | translate"
>
  <div
    class="text-sm text-gray-text overflow-hidden rounded-lg bg-white min-h-[400px] max-h-[600px] flex flex-col"
  >
    <!-- Header -->
    <div
      class="flex items-center justify-between px-4 py-3 text-white shrink-0"
      style="
        background: linear-gradient(
          135deg,
          var(--color-primary-start-color) 0%,
          var(--color-primary-end-color) 100%
        );
      "
    >
      <div class="flex items-center gap-2.5">
        @if (currentView() === 'runner') {
        <button
          (click)="backToMenu()"
          class="p-1 hover:bg-white/10 rounded-md transition-colors cursor-pointer border-0 bg-transparent text-white flex items-center"
        >
          <ic-chevron-left
            [width]="14"
            [height]="14"
            color="white"
          ></ic-chevron-left>
        </button>
        }
        <div class="flex flex-col">
          <div class="flex items-center gap-1.5 font-bold text-sm">
            <span>{{
              (currentView() === 'menu'
                ? 'e2eTestRunner'
                : selectedSuite()?.title || ''
              ) | translate
            }}</span>
            <span
              class="bg-white/20 text-white text-[9px] font-bold tracking-wide px-1.5 py-0.5 rounded border border-white/30"
              >DEV</span
            >
          </div>
          @if (currentView() === 'menu') {
          <span
            class="text-[10px] text-white/70 uppercase font-semibold tracking-wider"
            >{{ 'testSelection' | translate }}</span
          >
          } @else {
          <span
            class="text-[10px] text-white/70 uppercase font-semibold tracking-wider"
            >{{ 'runnerExecution' | translate }}</span
          >
          }
        </div>
      </div>
      <button
        class="bg-transparent border-0 text-white/80 cursor-pointer text-sm leading-none py-0.5 px-1 rounded transition-colors hover:bg-white/15 hover:text-white"
        (click)="panelRef.hide()"
        [attr.aria-label]="'close' | translate"
      >
        &#x2715;
      </button>
    </div>

    <!-- MAIN MENU VIEW -->
    @if (currentView() === 'menu') {
    <div
      class="flex-1 flex flex-col p-5 gap-5 animate-fade-in overflow-y-auto custom-scrollbar"
    >
      <div class="welcome-section">
        <h3 class="text-sm font-bold text-slate-800">
          {{ 'selectTestSuite' | translate }}
        </h3>
        <p class="text-[11px] text-slate-500 mt-1 leading-relaxed">
          Choose a test scenario to verify the system integrity and data flows.
        </p>
      </div>

      <div class="flex flex-col gap-3">
        @for (suite of testSuites; track suite.id) {
        <div appClickable class="suite-card group" (click)="selectSuite(suite)">
          <div class="suite-icon">
            @if (suite.iconType === 'manifest-template') {
            <ic-manifest-template
              [width]="14"
              [height]="14"
              startColor="var(--color-primary-start-color)"
              endColor="var(--color-primary-end-color)"
            ></ic-manifest-template>
            } @else if (suite.iconType === 'templates') {
            <ic-declaration-template
              [width]="14"
              [height]="14"
              startColor="var(--color-primary-start-color)"
              endColor="var(--color-primary-end-color)"
            ></ic-declaration-template>
            } @else if (suite.iconType === 'plane') {
            <ic-plane
              [width]="16"
              [height]="16"
              startColor="var(--color-primary-start-color)"
              endColor="var(--color-primary-end-color)"
            ></ic-plane>
            } @else {
            <ic-sparkles
              [width]="15"
              [height]="15"
              startColor="var(--color-primary-start-color)"
              endColor="var(--color-primary-end-color)"
            ></ic-sparkles>
            }
          </div>
          <div class="suite-info">
            <span class="suite-title">{{ suite.title | translate }}</span>
            <span class="suite-desc">{{ suite.description | translate }}</span>
          </div>
          <div class="suite-arrow">→</div>
        </div>
        }
      </div>
    </div>
    }

    <!-- RUNNER EXECUTION VIEW -->
    @if (currentView() === 'runner' && selectedSuite(); as suite) {
    <div class="flex-1 flex flex-col animate-fade-in min-h-0">
      <!-- Server status -->
      <div
        class="flex items-center gap-1.5 px-4 py-2 text-[11px] flex-wrap border-b border-line bg-white shrink-0"
        [ngClass]="
          serverAvailable()
            ? 'bg-green-50 text-green-700'
            : 'bg-amber-50 text-amber-800'
        "
      >
        <span
          class="w-[6px] h-[6px] rounded-full shrink-0"
          [ngClass]="serverAvailable() ? 'bg-st-tick' : 'bg-light-text'"
        ></span>
        <span>{{
          (serverAvailable() ? 'runnerServerOnline' : 'runnerServerOffline')
            | translate
        }}</span>
      </div>

      <!-- Run summary -->
      @if (summary(); as s) {
      <div
        class="flex border-b border-line bg-slate-50/50 shrink-0"
        [class.bg-rose-50]="s.failed > 0"
      >
        <div
          class="flex-1 flex flex-col items-center py-2.5 border-r border-line"
        >
          <span class="text-base font-bold leading-none text-st-tick">{{
            s.passed
          }}</span>
          <span
            class="text-[9px] uppercase tracking-wide text-light-text mt-1"
            >{{ 'passed' | translate }}</span
          >
        </div>
        <div
          class="flex-1 flex flex-col items-center py-2.5 border-r border-line"
        >
          <span class="text-base font-bold leading-none text-danger">{{
            s.failed
          }}</span>
          <span
            class="text-[9px] uppercase tracking-wide text-light-text mt-1"
            >{{ 'failed' | translate }}</span
          >
        </div>
        <div
          class="flex-1 flex flex-col items-center py-2.5 border-r border-line"
        >
          <span
            class="text-base font-bold leading-none text-accent-secondary"
            >{{ s.skipped }}</span
          >
          <span
            class="text-[9px] uppercase tracking-wide text-light-text mt-1"
            >{{ 'skipped' | translate }}</span
          >
        </div>
        <div class="flex-1 flex flex-col items-center py-2.5">
          <span class="text-base font-bold leading-none text-gray-text">{{
            s.total
          }}</span>
          <span
            class="text-[9px] uppercase tracking-wide text-light-text mt-1"
            >{{ 'total' | translate }}</span
          >
        </div>
      </div>
      } @if (lastRunAt()) {
      <div
        class="text-center text-[10px] text-light-text px-4 py-1.5 border-b border-line italic bg-white shrink-0"
      >
        {{ 'lastRun' | translate }}: {{ lastRunAt() | date : 'HH:mm:ss' }}
      </div>
      }

      <!-- Error message -->
      @if (errorMessage(); as err) {
      <div
        class="px-4 py-2 bg-rose-50 text-rose-700 text-[11px] whitespace-pre-wrap border-b border-rose-200 shrink-0"
      >
        {{ err }}
      </div>
      }

      <!-- Actions -->
      <div class="flex gap-2 px-4 py-4 border-b border-line bg-white shrink-0">
        <button
          class="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 border-0 rounded-lg text-white font-bold text-sm cursor-pointer transition-all hover:opacity-90 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
          [ngClass]="status() === 'running' ? 'bg-gray-text' : ''"
          [style.background]="
            status() !== 'running'
              ? 'linear-gradient(135deg, var(--color-primary-start-color) 0%, var(--color-primary-end-color) 100%)'
              : null
          "
          [disabled]="status() === 'running'"
          (click)="runTests()"
        >
          @if (status() === 'running') {
          <span
            class="inline-block w-3.5 h-3.5 rounded-full border-2 border-white/40 border-t-white animate-spin"
          ></span>
          {{ 'running' | translate }}
          } @else {
          <span>▶</span>
          {{ 'runTests' | translate }}
          }
        </button>

        @if (summary()) {
        <button
          class="py-2 px-3 border border-line rounded-lg bg-white text-gray-text text-xs font-semibold cursor-pointer transition-colors hover:bg-slate-50 flex items-center gap-1.5"
          (click)="openReport()"
        >
          📊 {{ 'report' | translate }}
        </button>
        }
      </div>

      <!-- Scrollable Content -->
      <div class="flex-1 overflow-y-auto custom-scrollbar p-4 bg-white">
        <!-- Currently running test -->
        @if (status() === 'running' && currentTest()) {
        <div
          class="flex items-center gap-2.5 p-3 bg-sky-50 border border-sky-100 rounded-lg text-xs text-sky-700 mb-4 animate-pulse"
        >
          <span
            class="inline-block shrink-0 w-2.5 h-2.5 rounded-full border-2 border-sky-200 border-t-sky-700 animate-spin"
          ></span>
          <span class="font-bold truncate">{{ currentTest() }}</span>
        </div>
        }

        <!-- Live log -->
        @if (liveLog().length > 0) {
        <div class="mb-5">
          <div
            class="font-bold text-[10px] uppercase tracking-widest text-light-text mb-2.5 flex items-center gap-1.5"
          >
            <span
              class="w-1.5 h-1.5 rounded-full bg-accent-secondary animate-pulse"
            ></span>
            {{ 'liveOutput' | translate }}
          </div>
          <div
            #logContainer
            class="max-h-[220px] overflow-y-auto bg-slate-900 rounded-xl p-3 font-mono text-[10px] leading-relaxed shadow-inner border border-slate-800"
          >
            @for (line of liveLog(); track $index) {
            <div
              class="whitespace-pre-wrap break-all py-0.5"
              [ngClass]="{
                'text-st-tick':
                  line.includes('passed') || line.startsWith('  ✓'),
                'text-danger':
                  line.includes('failed') ||
                  line.startsWith('  ✗') ||
                  line.startsWith('  ×'),
                'text-slate-400': !(
                  line.includes('passed') ||
                  line.startsWith('  ✓') ||
                  line.includes('failed') ||
                  line.startsWith('  ✗') ||
                  line.startsWith('  ×')
                )
              }"
            >
              {{ line }}
            </div>
            }
          </div>
        </div>
        }

        <!-- Scenarios list -->
        <div class="mb-4">
          <div
            class="font-bold text-[10px] uppercase tracking-widest text-light-text mb-2.5"
          >
            {{ 'scenarios' | translate }} ({{ suite.scenarios.length }})
          </div>
          <div class="grid gap-2">
            @for (scenario of suite.scenarios; track $index) {
            <div
              class="flex items-start gap-2.5 p-2 rounded-lg bg-slate-50/50 border border-slate-100/50"
            >
              <span class="text-[10px] mt-0.5 text-primary shrink-0">▶</span>
              <span class="text-[11px] text-gray-text leading-snug">{{
                scenario
              }}</span>
            </div>
            }
          </div>
        </div>
      </div>
    </div>
    }
  </div>
</app-popover>
